"use client"

import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeader } from '../utils/auth';
import { useOutletContext, Link } from 'react-router-dom';
import { useBranding } from "../hooks/useBranding";
import { isEnglishOperatorMode, withOperatorSurface } from "../utils/operatorMode";

// --- Icons ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const SendIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>;
const MailIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>;
const InfoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>;
const SettingsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>;
const RefreshIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21.5 2v6h-6"/><path d="M2.5 22v-6h6"/><path d="M21.5 8a10 10 0 0 0-20-4v2"/><path d="M2.5 16a10 10 0 0 0 20 4v-2"/></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const ChartBarIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"></line><line x1="18" y1="20" x2="18" y2="4"></line><line x1="6" y1="20" x2="6" y2="16"></line></svg>;
// 新規追加アイコン
const MagicIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>;
const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;
const CopyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>;
const LoadingSpinner = () => (
    <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
);


const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const EMAIL_COPY = {
    ja: {
        pageTitle: "メール配信管理 (MA)",
        openGem: "専用Gemを開く",
        htmlAssist: "HTMLメール作成アシスト",
        senderSettings: "送信元設定",
        newCampaign: "新規キャンペーン",
        usageTitle: "今月のメール配信状況",
        unit: "通",
        noCampaigns: "キャンペーンがありません。",
        mobileSent: "配信数",
        mobileCreated: "作成日",
        headers: ["ステータス", "キャンペーン名", "件名", "配信数", "作成日", "操作"],
        status: { draft: "下書き", scheduled: "予約済み", completed: "完了", failed: "失敗", sending: "Sending" },
        actions: { edit: "編集", send: "配信", delete: "削除", cancelSchedule: "予約を取消", report: "レポート", duplicate: "複製" },
        alerts: {
            saveFailed: "保存に失敗しました",
            deliverySetupFailed: "配信設定に失敗しました",
            updated: "更新しました。",
            draftSaved: "下書きを保存しました。",
            deleteConfirm: "このキャンペーンを削除しますか？\n削除すると元に戻せません。",
            deleted: "削除しました。",
            deleteFailed: "削除失敗",
            network: "通信エラーが発生しました。",
            limitReached: "メール送信上限に達しているため、配信を開始できません。",
            sendConfirm: (title) => `キャンペーン「${title}」の配信を開始しますか？\n\n※配信開始後はキャンセルできません。\n※通数制限を超過する場合はエラーになります。`,
            sendError: "配信エラー",
            cancelConfirm: (title) => `キャンペーン「${title}」の配信予約をキャンセルしますか？\n（下書き状態に戻ります）`,
            error: "エラー",
            reportFailed: "レポートの取得に失敗しました",
            reportNetwork: "通信エラー発生",
        },
        modal: {
            editTitle: "キャンペーン編集",
            newTitle: "新規メールキャンペーン作成",
            htmlAssistShort: "HTML生成アシスト",
            titleLabel: "キャンペーン名 (管理用)",
            titlePlaceholder: "例: 8月度メルマガ",
            fromLabel: "送信元 (From)",
            choose: "-- 選択 --",
            displayName: "表示名",
            targetSettings: "配信ターゲット設定",
            leads: "リード (見込み客)",
            customers: "既存顧客 (台帳)",
            allLeads: "全てのリード",
            segment: "条件指定",
            tagPlaceholder: "タグ (例: 展示会)",
            minScoreSuffix: "点以上",
            customerTagPlaceholder: "顧客タグ",
            allStatuses: "全ステータス",
            active: "契約中",
            churned: "解約済",
            subject: "件名",
            subjectPlaceholder: "件名を入力",
            body: "本文",
            code: "コード",
            preview: "プレビュー",
            availableTags: "使用可能タグ",
            timing: "配信タイミングの設定",
            deliveryTypes: { draft: "下書き保存", immediate: "今すぐ配信", scheduled: "予約配信" },
            scheduledAt: "配信開始日時 (日本時間)",
            usageTitle: "送信制限状況",
            remaining: "残り送信可能",
            monthlyLimit: "今月上限",
            testSend: "テスト送信",
            cancel: "キャンセル",
            updateSave: "更新して保存",
            saveDraft: "下書きを保存",
            startNow: "今すぐ配信を開始",
            schedule: "予約配信を設定",
            scheduleRequired: "予約配信の日時を指定してください。",
            defaultBody: `{{会社名}}\n{{宛名}}\n\nいつもお世話になっております。\n...`,
            duplicateSuffix: "コピー",
        },
        test: {
            title: "テスト送信",
            notesTitle: "確認事項",
            notes: ["{{会社名}} などの変数はダミーデータに置き換わります。", "HTMLメールの場合は崩れがないか確認してください。"],
            emailLabel: "送信先メールアドレス",
            cancel: "キャンセル",
            sending: "送信中...",
            submit: "テスト送信を実行",
            requireEmail: "送信先メールアドレスを入力してください。",
            requireContent: "件名と本文を入力してください。",
            success: "テスト送信が完了しました。\n受信ボックスを確認してください。",
            failed: "送信失敗",
            network: "通信エラーが発生しました。",
        },
        report: {
            loading: "レポートを集計中...",
            title: "配信レポート",
            summary: "サマリー",
            recipients: "受信者リスト",
            sent: "配信総数",
            delivered: "到達数",
            opens: "開封数",
            clicks: "クリック数",
            openRate: "開封率",
            clickRate: "クリック率",
            openAbout: "開封率について",
            clickAbout: "クリック率について",
            openHigh: "素晴らしい結果です！件名が魅力的だったようです。",
            openLow: "件名（Subject）を工夫することで改善できる可能性があります。",
            clickHigh: "高い関心を集めています！本文の内容やオファーが適切でした。",
            clickLow: "本文内のリンク位置や、誘導文言（CTA）を見直すと改善するかもしれません。",
            openBenchmark: (rate) => <>一般的なBtoBメルマガの平均開封率は <strong>15%〜25%</strong> 程度です。<br/>今回の結果は <strong>{rate}%</strong> です。</>,
            clickBenchmark: (rate) => <>一般的なクリック率の目安は <strong>1%〜3%</strong> です。<br/>今回の結果は <strong>{rate}%</strong> です。</>,
            columns: ["会社名 / お名前", "送信状態", "開封", "クリック", "リンク"],
            success: "成功",
            failed: "失敗",
            opened: "● 開封済",
            clicked: "● クリック済",
            detail: "詳細へ",
        },
        prompt: {
            title: "HTMLメール作成アシスト",
            lead: <>以下の項目を入力すると、AI（ChatGPTなど）に依頼するための「指示文（プロンプト）」を自動生成します。<br/>生成された指示文をAIに貼り付けるだけで、綺麗なHTMLメールが作れます。</>,
            theme: "メールのテーマ・目的",
            themePlaceholder: "例: 新サービスのリリース案内、Webセミナーの集客",
            target: "ターゲット層",
            targetPlaceholder: "例: 製造業の経営者、人事担当者",
            points: "伝えたいポイント（箇条書き推奨）",
            pointsPlaceholder: "・業務効率化を支援\n・初期設定の負担を軽減\n・導入後の運用をサポート",
            tone: "雰囲気（トーン）",
            color: "メインカラー",
            tones: {
                professional: ["信頼感・プロフェッショナル", "信頼感のある、プロフェッショナルな"],
                friendly: ["親しみやすい・柔らかい", "親しみやすい、柔らかい"],
                urgent: ["緊急・限定感", "緊急性を強調した、限定感のある"],
                creative: ["クリエイティブ・洗練", "クリエイティブで洗練された"],
            },
            colors: {
                blue: ["ブルー系 (信頼)", "青（信頼・誠実）"],
                green: ["グリーン系 (安心)", "緑（安心・成長）"],
                orange: ["オレンジ系 (活気)", "オレンジ（活気・親近感）"],
                black: ["モノトーン (高級感)", "モノトーン（高級感・モダン）"],
            },
            ctaSettings: "ボタン（CTA）の設定",
            ctaLabelPlaceholder: "ボタンの文言 (例: 資料をダウンロード)",
            ctaUrlPlaceholder: "リンク先URL (例: https://example.com/...)",
            ctaDefaultLabel: "詳しくはこちら",
            ctaDefaultUrl: "{{リンク}}",
            addButton: "ボタンを追加する",
            generate: "プロンプトを生成する",
            doneTitle: "プロンプト生成完了！",
            doneLead: "下のテキストをコピーして、ChatGPTやGeminiに貼り付けてください。",
            copied: "コピー済み",
            copy: "コピー",
            officialAssistant: "SalesBreaker 公式アシスタント",
            hint: "ヒント:",
            tips: (appName) => [`AIが生成したHTMLコードをコピーし、${appName}の本文入力欄（プレビュー機能付き）に貼り付けてください。`, "画像URLの部分は、実際の画像のURLに書き換えてください。"],
            back: "条件を修正する",
            close: "閉じる",
        },
    },
    en: {
        pageTitle: "Email Campaigns",
        openGem: "Open Dedicated Gem",
        htmlAssist: "HTML Email Assist",
        senderSettings: "Sender Settings",
        newCampaign: "New Campaign",
        usageTitle: "Monthly Email Usage",
        unit: "emails",
        noCampaigns: "No campaigns yet.",
        mobileSent: "Sent",
        mobileCreated: "Created",
        headers: ["Status", "Campaign", "Subject", "Sent", "Created", "Actions"],
        status: { draft: "Draft", scheduled: "Scheduled", completed: "Completed", failed: "Failed", sending: "Sending" },
        actions: { edit: "Edit", send: "Send", delete: "Delete", cancelSchedule: "Cancel Schedule", report: "Report", duplicate: "Duplicate" },
        alerts: {
            saveFailed: "Save failed",
            deliverySetupFailed: "Delivery setup failed",
            updated: "Updated.",
            draftSaved: "Draft saved.",
            deleteConfirm: "Delete this campaign?\nThis cannot be undone.",
            deleted: "Deleted.",
            deleteFailed: "Delete failed",
            network: "Network error.",
            limitReached: "Email sending limit has been reached. You cannot start delivery.",
            sendConfirm: (title) => `Start delivery for "${title}"?\n\nDelivery cannot be canceled once it starts.\nIf the campaign exceeds your sending limit, delivery may fail.`,
            sendError: "Delivery error",
            cancelConfirm: (title) => `Cancel scheduled delivery for "${title}"?\nIt will return to draft.`,
            error: "Error",
            reportFailed: "Failed to load report",
            reportNetwork: "Network error",
        },
        modal: {
            editTitle: "Edit Campaign",
            newTitle: "Create Email Campaign",
            htmlAssistShort: "HTML Assist",
            titleLabel: "Campaign Name (internal)",
            titlePlaceholder: "Example: August newsletter",
            fromLabel: "From Profile",
            choose: "-- Select --",
            displayName: "Display Name",
            targetSettings: "Delivery Target",
            leads: "Leads (prospects)",
            customers: "Existing Customers (CRM)",
            allLeads: "All leads",
            segment: "Segment",
            tagPlaceholder: "Tags (example: webinar)",
            minScoreSuffix: "points or more",
            customerTagPlaceholder: "Customer tags",
            allStatuses: "All statuses",
            active: "Active",
            churned: "Churned",
            subject: "Subject",
            subjectPlaceholder: "Enter subject",
            body: "Body",
            code: "Code",
            preview: "Preview",
            availableTags: "Available tags",
            timing: "Delivery Timing",
            deliveryTypes: { draft: "Save Draft", immediate: "Send Now", scheduled: "Schedule" },
            scheduledAt: "Start date/time (JST)",
            usageTitle: "Sending Limit",
            remaining: "Remaining",
            monthlyLimit: "Monthly limit",
            testSend: "Test Send",
            cancel: "Cancel",
            updateSave: "Update and Save",
            saveDraft: "Save Draft",
            startNow: "Start Delivery Now",
            schedule: "Schedule Delivery",
            scheduleRequired: "Please select a scheduled delivery date and time.",
            defaultBody: `{{会社名}}\n{{宛名}}\n\nHello,\n\nThank you for your time.\n...`,
            duplicateSuffix: "copy",
        },
        test: {
            title: "Test Send",
            notesTitle: "Checklist",
            notes: ["Variables such as {{会社名}} are replaced with sample data.", "For HTML emails, check the layout in your inbox."],
            emailLabel: "Recipient Email Address",
            cancel: "Cancel",
            sending: "Sending...",
            submit: "Send Test Email",
            requireEmail: "Enter a recipient email address.",
            requireContent: "Enter both subject and body.",
            success: "Test email sent.\nPlease check your inbox.",
            failed: "Send failed",
            network: "Network error.",
        },
        report: {
            loading: "Calculating report...",
            title: "Delivery Report",
            summary: "Summary",
            recipients: "Recipient List",
            sent: "Total Sent",
            delivered: "Delivered",
            opens: "Opens",
            clicks: "Clicks",
            openRate: "Open rate",
            clickRate: "Click rate",
            openAbout: "About open rate",
            clickAbout: "About click rate",
            openHigh: "Strong result. The subject line likely worked well.",
            openLow: "You may be able to improve this by refining the subject line.",
            clickHigh: "Strong engagement. The message and offer appear to be relevant.",
            clickLow: "Consider improving link placement or CTA wording.",
            openBenchmark: (rate) => <>A typical B2B newsletter open rate is around <strong>15%-25%</strong>.<br/>This campaign is at <strong>{rate}%</strong>.</>,
            clickBenchmark: (rate) => <>A common click-rate benchmark is around <strong>1%-3%</strong>.<br/>This campaign is at <strong>{rate}%</strong>.</>,
            columns: ["Company / Name", "Delivery Status", "Opened", "Clicked", "Link"],
            success: "Success",
            failed: "Failed",
            opened: "Opened",
            clicked: "Clicked",
            detail: "Details",
        },
        prompt: {
            title: "HTML Email Assist",
            lead: <>Fill in the fields below to generate a prompt for your AI assistant.<br/>Use the generated prompt to create a polished HTML email.</>,
            theme: "Email theme / purpose",
            themePlaceholder: "Example: New service announcement, webinar invitation",
            target: "Target audience",
            targetPlaceholder: "Example: B2B founders, Japan market-entry teams",
            points: "Key points to communicate",
            pointsPlaceholder: "- Help teams expand into Japan\n- Reduce setup work\n- Support follow-up operations after launch",
            tone: "Tone",
            color: "Main color",
            tones: {
                professional: ["Professional / credible", "professional and credible"],
                friendly: ["Friendly / approachable", "friendly and approachable"],
                urgent: ["Urgent / limited", "urgent with a limited-offer feel"],
                creative: ["Creative / refined", "creative and refined"],
            },
            colors: {
                blue: ["Blue (trust)", "blue, expressing trust and reliability"],
                green: ["Green (safety)", "green, expressing safety and growth"],
                orange: ["Orange (energy)", "orange, expressing energy and approachability"],
                black: ["Monotone (premium)", "monotone, expressing a premium and modern feel"],
            },
            ctaSettings: "CTA Button Settings",
            ctaLabelPlaceholder: "Button label (example: Download the guide)",
            ctaUrlPlaceholder: "Destination URL (example: https://example.com/...)",
            ctaDefaultLabel: "Learn more",
            ctaDefaultUrl: "{{link}}",
            addButton: "Add Button",
            generate: "Generate Prompt",
            doneTitle: "Prompt Ready",
            doneLead: "Copy the text below and use it with your agent or AI assistant.",
            copied: "Copied",
            copy: "Copy",
            officialAssistant: "SalesBreaker Official Assistant",
            hint: "Hint:",
            tips: (appName) => [`Copy the HTML generated by AI and paste it into the ${appName} body field with preview enabled.`, "If the AI includes image URLs, replace them with your actual image URLs."],
            back: "Edit Conditions",
            close: "Close",
        },
    },
};

const getEmailCopy = (isEnglishBeta) => isEnglishBeta ? EMAIL_COPY.en : EMAIL_COPY.ja;

// --- ★★★ HTMLメール作成アシストモーダル ★★★ ---
function HtmlPromptGeneratorModal({ onClose, isEnglishBeta = false }) {
    const { appName } = useBranding();
    const copy = getEmailCopy(isEnglishBeta).prompt;
    // ... (変更なし)
    const [step, setStep] = useState(1); // 1: 入力, 2: 生成結果
    const [formData, setFormData] = useState({
        theme: '', // テーマ・目的
        target: '', // ターゲット層
        points: '', // 訴求ポイント・内容
        tone: 'professional', // トーン＆マナー
        color: 'blue', // メインカラー
    });

    // ★ 追加: ボタン（文言とURL）のセットを管理するstate
    const [ctas, setCtas] = useState([{ label: '', url: '' }]);

    // ★ 追加: ボタン入力欄を増やす関数
    const addCta = () => setCtas([...ctas, { label: '', url: '' }]);

    // ★ 追加: ボタン入力欄を減らす関数（最低1つは残す）
    const removeCta = (index) => {
        if (ctas.length > 1) {
            setCtas(ctas.filter((_, i) => i !== index));
        }
    };

    // ★ 追加: ボタンの入力内容を更新する関数
    const handleCtaChange = (index, field, value) => {
        const newCtas = [...ctas];
        newCtas[index][field] = value;
        setCtas(newCtas);
    };
    const [generatedPrompt, setGeneratedPrompt] = useState('');
    const [isCopied, setIsCopied] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const generatePrompt = () => {
        const toneMap = {
            professional: copy.tones.professional[1],
            friendly: copy.tones.friendly[1],
            urgent: copy.tones.urgent[1],
            creative: copy.tones.creative[1]
        };

        const colorMap = {
            blue: copy.colors.blue[1],
            green: copy.colors.green[1],
            orange: copy.colors.orange[1],
            black: copy.colors.black[1]
        };

        const ctaInstructions = ctas.map((c, i) => 
            isEnglishBeta
                ? `- Button ${i + 1}: label "${c.label || copy.ctaDefaultLabel}", URL "${c.url || copy.ctaDefaultUrl}"`
                : `- ボタン${i + 1}: 文言「${c.label || copy.ctaDefaultLabel}」、URL「${c.url || copy.ctaDefaultUrl}」`
        ).join('\n');

        // ▼▼▼ footerInfo は不要になったので削除 ▼▼▼

        const prompt = isEnglishBeta ? `
You are a professional web designer and copywriter.
Create the source code for a responsive HTML email for B2B sales and marketing based on the requirements below.

# Requirements
- **Purpose / theme**: ${formData.theme}
- **Target audience**: ${formData.target}
- **Key points**:
${formData.points}
- **Design tone**: ${toneMap[formData.tone]}
- **Main color**: ${colorMap[formData.color]}

# CTA Buttons
${ctaInstructions}

# Important footer rule
The system automatically appends issuer information, company address, and an unsubscribe link to the end of the email.
Do not create a footer, signature, company information block, or unsubscribe section. Only create the pure content body.

# Technical requirements
- Use a table layout (<table>) so the email renders reliably in Outlook, Gmail, and other email clients.
- Use inline CSS with style attributes.
- Do not use images. Build a polished, professional design using only background colors, borders, button colors, font sizes, and spacing.
- Make the layout responsive for mobile devices.
- Keep the maximum content width at 600px.

# Output
- Output HTML code only. Do not include explanations.
` : `
あなたはプロのWebデザイナー兼コピーライターです。
以下の要件に基づいて、BtoB営業用の「HTMLメール（レスポンシブ対応）」のソースコードを作成してください。

# 制作要件
- **目的・テーマ**: ${formData.theme}
- **ターゲット層**: ${formData.target}
- **訴求ポイント**: 
${formData.points}
- **デザインの雰囲気**: ${toneMap[formData.tone]}
- **メインカラー**: ${colorMap[formData.color]}
# CTAボタン（クリックエリア）
${ctaInstructions}

# 【重要】フッターについて
システム側で自動的に「発行元情報（会社名・住所）」と「配信停止リンク」をメールの末尾に挿入します。
そのため、AI側ではフッター（署名や会社情報など）を絶対に作成しないでください。純粋なコンテンツ部分のみを作成してください。

# 技術仕様
- メーラー（Outlook, Gmail等）での表示崩れを防ぐため、テーブルレイアウト（<table>）を使用してください。
- CSSはインラインスタイル（style属性）で記述してください。
- **画像は一切使用しないでください。** 背景色、枠線、ボタンの配色、フォントサイズの強弱のみで、画像がなくてもプロフェッショナルで高級感のあるデザインを構築してください。
- レスポンス対応（スマホで見た際は幅100%になるなど）を考慮してください。
- 全体の横幅は600pxを上限としてください。

# 出力内容
- HTMLコードのみを出力してください。（解説は不要です）
`;
        setGeneratedPrompt(prompt.trim());
        setStep(2);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedPrompt);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-3 sm:p-4">
            <div className="bg-white w-full max-w-lg rounded-2xl sm:rounded-lg shadow-xl flex flex-col max-h-[92vh] sm:max-h-[90vh]">
                <div className="flex justify-between items-start gap-4 p-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-2xl sm:rounded-t-lg">
                    <h3 className="font-bold text-base sm:text-lg flex items-center gap-2">
                        <MagicIcon /> {copy.title}
                    </h3>
                    <button onClick={onClose} className="shrink-0 text-white hover:text-gray-200 text-2xl leading-none opacity-80 hover:opacity-100">&times;</button>
                </div>

                <div className="p-4 sm:p-6 overflow-y-auto">
                    {step === 1 ? (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-100">
                                {copy.lead}
                            </p>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">{copy.theme} <span className="text-red-500">*</span></label>
                                <input 
                                    type="text" name="theme" 
                                    className="w-full p-2 border rounded text-sm" 
                                    placeholder={copy.themePlaceholder}
                                    value={formData.theme} onChange={handleChange} 
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">{copy.target}</label>
                                <input 
                                    type="text" name="target" 
                                    className="w-full p-2 border rounded text-sm" 
                                    placeholder={copy.targetPlaceholder}
                                    value={formData.target} onChange={handleChange} 
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">{copy.points}</label>
                                <textarea 
                                    name="points" rows="3" 
                                    className="w-full p-2 border rounded text-sm" 
                                    placeholder={copy.pointsPlaceholder}
                                    value={formData.points} onChange={handleChange} 
                                ></textarea>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">{copy.tone}</label>
                                    <select name="tone" className="w-full p-2 border rounded text-sm bg-white" value={formData.tone} onChange={handleChange}>
                                        <option value="professional">{copy.tones.professional[0]}</option>
                                        <option value="friendly">{copy.tones.friendly[0]}</option>
                                        <option value="urgent">{copy.tones.urgent[0]}</option>
                                        <option value="creative">{copy.tones.creative[0]}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">{copy.color}</label>
                                    <select name="color" className="w-full p-2 border rounded text-sm bg-white" value={formData.color} onChange={handleChange}>
                                        <option value="blue">{copy.colors.blue[0]}</option>
                                        <option value="green">{copy.colors.green[0]}</option>
                                        <option value="orange">{copy.colors.orange[0]}</option>
                                        <option value="black">{copy.colors.black[0]}</option>
                                    </select>
                                </div>
                            </div>

                           <div className="space-y-3">
                                <label className="block text-sm font-bold text-gray-700">{copy.ctaSettings}</label>
                                {ctas.map((cta, index) => (
                                    <div key={index} className="flex gap-2 items-start animate-fadeIn">
                                        <div className="flex-1 space-y-2">
                                            <input 
                                                type="text" 
                                                className="w-full p-2 border rounded text-xs" 
                                                placeholder={copy.ctaLabelPlaceholder}
                                                value={cta.label} 
                                                onChange={(e) => handleCtaChange(index, 'label', e.target.value)} 
                                            />
                                            <input 
                                                type="text" 
                                                className="w-full p-2 border rounded text-xs font-mono bg-gray-50" 
                                                placeholder={copy.ctaUrlPlaceholder}
                                                value={cta.url} 
                                                onChange={(e) => handleCtaChange(index, 'url', e.target.value)} 
                                            />
                                        </div>
                                        {ctas.length > 1 && (
                                            <button 
                                                onClick={() => removeCta(index)}
                                                className="text-red-400 hover:text-red-600 p-1"
                                            >
                                                &times;
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button 
                                    type="button"
                                    onClick={addCta}
                                    className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 py-1"
                                >
                                    <PlusIcon /> {copy.addButton}
                                </button>
                            </div>

                            <div className="pt-4">
                                <button 
                                    onClick={generatePrompt} 
                                    disabled={!formData.theme}
                                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-3 rounded hover:opacity-90 disabled:opacity-50 transition-all flex justify-center items-center gap-2 shadow-md"
                                >
                                    <MagicIcon /> {copy.generate}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="text-center mb-4">
                                <h4 className="font-bold text-lg text-gray-800">{copy.doneTitle}</h4>
                                <p className="text-sm text-gray-600 mt-1">{copy.doneLead}</p>
                            </div>

                            <div className="relative">
                                <textarea 
                                    readOnly 
                                    className="w-full h-60 sm:h-64 p-4 bg-gray-50 text-gray-800 font-mono text-xs rounded border border-gray-300 focus:ring-0 resize-none"
                                    value={generatedPrompt}
                                ></textarea>
                                <button 
                                    onClick={handleCopy} 
                                    className={`absolute top-2 right-2 px-3 py-1.5 rounded text-xs font-bold shadow transition-all flex items-center gap-1 ${isCopied ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border'}`}
                                >
                                    {isCopied ? <><CheckIcon /> {copy.copied}</> : <><CopyIcon /> {copy.copy}</>}
                                </button>
                            </div>

                            {!isEnglishBeta && <div className="mt-4">
                                <a 
                                    href="https://gemini.google.com/gem/1ZWnJbH7arFMPx8VMYv_8FxBaBkuEAKgk?usp=sharing" 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="flex flex-col items-center justify-center gap-1 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 text-blue-700 rounded-3xl hover:border-blue-400 hover:shadow-md transition-all w-full"
                                >
                                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80">
                                        {copy.officialAssistant}
                                    </span>
                                    <span className="text-sm font-bold flex items-center gap-2">
                                        <MagicIcon /> {getEmailCopy(isEnglishBeta).openGem}
                                    </span>
                                </a>
                            </div>}

                            <div className="bg-yellow-50 p-3 rounded border border-yellow-200 text-xs text-yellow-800">
                                <strong>{copy.hint}</strong>
                                <ul className="list-disc pl-4 mt-1 space-y-1">
                                    {copy.tips(appName).map((tip) => <li key={tip}>{tip}</li>)}
                                </ul>
                            </div>

                            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-between sm:items-center">
                                <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700 underline">{copy.back}</button>
                                <button onClick={onClose} className="w-full bg-gray-800 text-white font-bold py-2 px-6 rounded hover:bg-gray-900 sm:w-auto">{copy.close}</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- テスト送信モーダル ---
function TestSendModal({ onClose, campaignData, userEmail, isEnglishBeta = false }) {
    // ... (変更なし)
    const copy = getEmailCopy(isEnglishBeta).test;
    const [targetEmail, setTargetEmail] = useState(userEmail || '');
    const [isSending, setIsSending] = useState(false);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!targetEmail) return alert(copy.requireEmail);
        if (!campaignData.subject || !campaignData.body) return alert(copy.requireContent);

        setIsSending(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/campaigns/test-send`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({
                    subject: campaignData.subject,
                    body: campaignData.body,
                    target_email: targetEmail,
                    issuer_profile_id: campaignData.issuer_profile_id
                })
            });
            const data = await res.json();
            if (data.success) {
                alert(copy.success);
                onClose();
            } else {
                alert(`${copy.failed}: ${data.error}`);
            }
        } catch (err) {
            alert(copy.network);
            console.error(err);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-lg shadow-xl p-5 sm:p-6">
                <div className="flex justify-between items-center mb-4 border-b pb-2">
                    <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                        <SendIcon /> {copy.title}
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <CloseIcon />
                    </button>
                </div>
                
                <form onSubmit={handleSend} className="space-y-4">
                    <div className="bg-yellow-50 p-3 rounded text-xs text-yellow-800 border border-yellow-200">
                        <p className="font-bold">{copy.notesTitle}</p>
                        <ul className="list-disc pl-4 mt-1 space-y-1">
                            {copy.notes.map((note) => <li key={note}>{note}</li>)}
                        </ul>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">{copy.emailLabel}</label>
                        <input 
                            type="email" 
                            required 
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none" 
                            value={targetEmail} 
                            onChange={(e) => setTargetEmail(e.target.value)} 
                            placeholder="your-email@example.com"
                        />
                    </div>

                    <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            className="w-full px-4 py-2 border rounded text-gray-600 hover:bg-gray-50 sm:w-auto"
                            disabled={isSending}
                        >
                            {copy.cancel}
                        </button>
                        <button 
                            type="submit" 
                            className="w-full justify-center px-6 py-2 bg-indigo-600 text-white font-bold rounded hover:bg-indigo-700 disabled:bg-gray-400 flex items-center gap-2 sm:w-auto"
                            disabled={isSending}
                        >
                            {isSending ? <RefreshIcon className="animate-spin" /> : <SendIcon />}
                            {isSending ? copy.sending : copy.submit}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// --- レポートモーダル ---
function ReportModal({
    onClose,
    data,
    isLoading,
    title,
    campaignId,
    isEnglishBeta = false,
    customersPath = '/customers',
    leadsPath = '/marketing/leads',
}) {
    const copy = getEmailCopy(isEnglishBeta).report;
    const [recipients, setRecipients] = useState([]);
    const [isListLoading, setIsListLoading] = useState(false);
    const [viewMode, setViewMode] = useState('summary'); // 'summary' or 'list'

    // 受信者リストの取得
    const fetchRecipients = async () => {
        setIsListLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}/recipients`, { headers: getAuthHeader() });
            const result = await res.json();
            if (result.success) setRecipients(result.recipients);
        } catch (e) { console.error(e); }
        finally { setIsListLoading(false); }
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white p-8 rounded-lg shadow-xl flex flex-col items-center">
                    <RefreshIcon className="animate-spin h-8 w-8 text-blue-600 mb-4" />
                    <p className="text-gray-600 font-bold">{copy.loading}</p>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const { sent, failed, opens, clicks } = data;
    const delivered = Math.max(0, sent - failed);
    const openRate = delivered > 0 ? ((opens / delivered) * 100).toFixed(1) : 0;
    const clickRate = delivered > 0 ? ((clicks / delivered) * 100).toFixed(1) : 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-4xl rounded-2xl sm:rounded-lg shadow-xl flex flex-col max-h-[92vh] sm:max-h-[90vh]">
                <div className="flex flex-col gap-3 p-4 border-b bg-gray-50 sm:flex-row sm:justify-between sm:items-center">
                    <h3 className="font-bold text-base sm:text-lg text-gray-800 flex items-center gap-2 break-words">
                        <ChartBarIcon /> {copy.title}: {title}
                    </h3>
                    <div className="flex items-center justify-between gap-2 sm:justify-end">
                        <div className="bg-white border rounded p-1 grid grid-cols-2 flex-1 sm:flex-none sm:flex">
                            <button 
                                onClick={() => setViewMode('summary')}
                                className={`px-3 py-1 text-xs font-bold rounded ${viewMode === 'summary' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                            >{copy.summary}</button>
                            <button 
                                onClick={() => { setViewMode('list'); if (recipients.length === 0) fetchRecipients(); }}
                                className={`px-3 py-1 text-xs font-bold rounded ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                            >{copy.recipients}</button>
                        </div>
                        <button onClick={onClose} className="text-gray-500 hover:bg-gray-200 rounded-full p-1"><CloseIcon /></button>
                    </div>
                </div>
                
                <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
                    {viewMode === 'summary' ? (
                        <>
                            {/* --- サマリー表示（既存のカード部分） --- */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                <div className="bg-gray-50 p-4 rounded-lg text-center border">
                                    <p className="text-xs text-gray-500 font-bold uppercase">{copy.sent}</p>
                                    <p className="text-2xl font-bold text-gray-800">{sent.toLocaleString()}</p>
                                </div>
                                <div className="bg-blue-50 p-4 rounded-lg text-center border border-blue-100">
                                    <p className="text-xs text-blue-600 font-bold uppercase">{copy.delivered}</p>
                                    <p className="text-2xl font-bold text-blue-700">{delivered.toLocaleString()}</p>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-lg text-center border border-yellow-100 cursor-pointer hover:bg-yellow-100" onClick={() => { setViewMode('list'); fetchRecipients(); }}>
                                    <p className="text-xs text-yellow-600 font-bold uppercase">{copy.opens}</p>
                                    <p className="text-2xl font-bold text-yellow-700">{opens.toLocaleString()}</p>
                                    <p className="text-xs text-yellow-600 mt-1 font-bold">{copy.openRate}: {openRate}%</p>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg text-center border border-green-100 cursor-pointer hover:bg-green-100" onClick={() => { setViewMode('list'); fetchRecipients(); }}>
                                    <p className="text-xs text-green-600 font-bold uppercase">{copy.clicks}</p>
                                    <p className="text-2xl font-bold text-green-700">{clicks.toLocaleString()}</p>
                                    <p className="text-xs text-green-600 mt-1 font-bold">{copy.clickRate}: {clickRate}%</p>
                                </div>
                            </div>
                            {/* 詳細解説 */}
                    <div className="space-y-4">
                        <div className="bg-white border rounded-lg p-4">
                            <h4 className="font-bold text-gray-700 mb-2 flex items-center gap-2">
                                <span className="w-2 h-6 bg-yellow-400 rounded-sm"></span>
                                {copy.openAbout}
                            </h4>
                            <p className="text-sm text-gray-600">
                                {copy.openBenchmark(openRate)} {Number(openRate) >= 20 ? copy.openHigh : copy.openLow}
                            </p>
                        </div>

                        <div className="bg-white border rounded-lg p-4">
                            <h4 className="font-bold text-gray-700 mb-2 flex items-center gap-2">
                                <span className="w-2 h-6 bg-green-500 rounded-sm"></span>
                                {copy.clickAbout}
                            </h4>
                            <p className="text-sm text-gray-600">
                                {copy.clickBenchmark(clickRate)} {Number(clickRate) >= 2 ? copy.clickHigh : copy.clickLow}
                            </p>
                        </div>
                    </div>
                        </>
                    ) : (
                        /* --- 詳細リスト表示（新規追加） --- */
                        <div className="space-y-4">
                            {isListLoading ? <LoadingSpinner /> : (
                                <div className="overflow-x-auto">
                                <table className="w-full min-w-[720px] text-xs text-left text-gray-600">
                                    <thead className="bg-gray-100 text-gray-700 sticky top-0">
                                        <tr>
                                            <th className="p-3">{copy.columns[0]}</th>
                                            <th className="p-3">{copy.columns[1]}</th>
                                            <th className="p-3 text-center">{copy.columns[2]}</th>
                                            <th className="p-3 text-center">{copy.columns[3]}</th>
                                            <th className="p-3">{copy.columns[4]}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {recipients.map((r, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="p-3">
                                                    <div className="font-bold">{r.company_name}</div>
                                                    <div className="text-gray-500">{r.recipient_name}</div>
                                                </td>
                                                <td className="p-3">
                                                    <span className={`px-2 py-0.5 rounded ${r.status === 'sent' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                                        {r.status === 'sent' ? copy.success : copy.failed}
                                                    </span>
                                                    {r.error_message && <div className="text-[10px] text-red-400 mt-1">{r.error_message}</div>}
                                                </td>
                                                <td className="p-3 text-center">
                                                    {r.is_opened ? <span className="text-yellow-500 font-bold">{copy.opened}</span> : <span className="text-gray-300">-</span>}
                                                </td>
                                                <td className="p-3 text-center">
                                                    {r.is_clicked ? <span className="text-green-600 font-bold">{copy.clicked}</span> : <span className="text-gray-300">-</span>}
                                                </td>
                                                <td className="p-3">
                                                    {/* 各詳細ページへのリンク（遷移先は要確認） */}
                                                    <Link 
                                                        to={r.customer_id ? withOperatorSurface(customersPath) : withOperatorSurface(leadsPath)}
                                                        className="text-blue-600 hover:underline"
                                                    >
                                                        {copy.detail}
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- キャンペーン作成モーダル (HTMLプレビュー付き & セグメント設定追加) ---
// ★ 修正: issuerProfiles を受け取るように変更
function CampaignModal({ onClose, onSave, usage, userEmail, openPromptGenerator, campaign, issuerProfiles = [], isEnglishBeta = false }) {
    const pageCopy = getEmailCopy(isEnglishBeta);
    const copy = pageCopy.modal;
    const [form, setForm] = useState({
        title: campaign?.title || '',
        sender_name: campaign?.sender_name || '',
        issuer_profile_id: campaign?.issuer_profile_id || '',
        subject: campaign?.subject || '',
        body: campaign?.body || copy.defaultBody,
        target_filter: campaign?.target_filter 
            ? (typeof campaign.target_filter === 'string' ? JSON.parse(campaign.target_filter) : campaign.target_filter)
            : { type: 'all', tags: '', min_score: 0, source_type: 'lead' },
        // 配信設定の初期値
        delivery_type: 'draft', // 'draft', 'immediate', 'scheduled'
        scheduled_at: '' 
    });
    const [showTestModal, setShowTestModal] = useState(false);
    const [viewMode, setViewMode] = useState('code');

    const handleProfileChange = (e) => {
        const profileId = e.target.value;
        const profile = issuerProfiles.find(p => String(p.id) === String(profileId));
        setForm(prev => ({
            ...prev,
            issuer_profile_id: profileId,
            sender_name: profile ? (profile.company_name || profile.profile_name) : prev.sender_name
        }));
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            target_filter: { ...prev.target_filter, [name]: value }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // 予約配信なのに時間が未入力の場合は止める
        if (form.delivery_type === 'scheduled' && !form.scheduled_at) {
            return alert(copy.scheduleRequired);
        }
        onSave(form);
    };

    const remainingSends = usage ? usage.email.limit - usage.email.current : 0;
    const isOverLimit = remainingSends <= 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white w-full max-w-4xl rounded-2xl sm:rounded-lg shadow-xl flex flex-col max-h-[92vh] sm:max-h-[90vh]">
                {/* ヘッダー */}
                <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:justify-between sm:items-center">
                    <h3 className="font-bold text-base sm:text-lg">{campaign && campaign.id ? copy.editTitle : copy.newTitle}</h3>
                    <div className="grid grid-cols-1 gap-2 sm:flex">
                        {!isEnglishBeta && (
                            <a
                                href="https://gemini.google.com/gem/1ZWnJbH7arFMPx8VMYv_8FxBaBkuEAKgk?usp=sharing"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1 rounded text-xs font-bold flex items-center gap-1 transition-colors"
                            >
                                <MagicIcon /> {pageCopy.openGem}
                            </a>
                        )}
                        {!isEnglishBeta && (
                            <button type="button" onClick={openPromptGenerator} className="bg-purple-100 text-purple-700 hover:bg-purple-200 px-3 py-1 rounded text-xs font-bold flex items-center gap-1">
                                <MagicIcon /> {copy.htmlAssistShort}
                            </button>
                        )}
                        <button onClick={onClose} className="rounded bg-gray-100 px-3 py-1 text-gray-500 text-2xl leading-none hover:text-gray-700 sm:bg-transparent">&times;</button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto flex-grow flex flex-col gap-6">
                    {/* 基本設定：タイトル・送信元 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold mb-1">{copy.titleLabel}</label>
                            <input type="text" name="title" required className="w-full p-2 border rounded" placeholder={copy.titlePlaceholder} value={form.title} onChange={handleChange} />
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-bold mb-1">{copy.fromLabel}</label>
                                <select name="issuer_profile_id" required className="w-full p-2 border rounded bg-white text-sm" value={form.issuer_profile_id} onChange={handleProfileChange}>
                                    <option value="">{copy.choose}</option>
                                    {issuerProfiles.map(p => <option key={p.id} value={p.id}>{p.profile_name} ({p.sender_email})</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-1">{copy.displayName}</label>
                                <input type="text" name="sender_name" required className="w-full p-2 border rounded" value={form.sender_name} onChange={handleChange} />
                            </div>
                        </div>
                    </div>

                    {/* ターゲット設定 */}
                    <div className="bg-gray-50 p-4 rounded border border-gray-200">
                        <label className="block text-sm font-bold mb-3 flex items-center gap-2"><MailIcon /> {copy.targetSettings}</label>
                        <div className="grid grid-cols-1 gap-3 mb-4 sm:grid-cols-2">
                            <label className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded border transition-all ${form.target_filter?.source_type !== 'customer' ? 'bg-blue-50 border-blue-400' : 'bg-white'}`}>
                                <input type="radio" checked={form.target_filter?.source_type !== 'customer'} onChange={() => setForm(prev => ({...prev, target_filter: {...prev.target_filter, source_type: 'lead'}}))} />
                                <span className="text-sm font-bold">{copy.leads}</span>
                            </label>
                            <label className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded border transition-all ${form.target_filter?.source_type === 'customer' ? 'bg-green-50 border-green-400' : 'bg-white'}`}>
                                <input type="radio" checked={form.target_filter?.source_type === 'customer'} onChange={() => setForm(prev => ({...prev, target_filter: {...prev.target_filter, source_type: 'customer'}}))} />
                                <span className="text-sm font-bold">{copy.customers}</span>
                            </label>
                        </div>

                        {form.target_filter?.source_type !== 'customer' ? (
                            <div className="space-y-3 pt-2 border-t">
                                <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                                    <label className="flex items-center gap-2"><input type="radio" name="type" value="all" checked={form.target_filter.type === 'all'} onChange={handleFilterChange} /><span className="text-sm">{copy.allLeads}</span></label>
                                    <label className="flex items-center gap-2"><input type="radio" name="type" value="segment" checked={form.target_filter.type === 'segment'} onChange={handleFilterChange} /><span className="text-sm font-bold">{copy.segment}</span></label>
                                </div>
                                {form.target_filter.type === 'segment' && (
                                    <div className="grid grid-cols-1 gap-3 animate-fadeIn sm:grid-cols-2 sm:pl-6">
                                        <input type="text" name="tags" className="p-2 border rounded text-sm" placeholder={copy.tagPlaceholder} value={form.target_filter.tags} onChange={handleFilterChange} />
                                        <div className="flex items-center gap-2"><input type="number" name="min_score" className="w-20 p-2 border rounded text-sm" value={form.target_filter.min_score} onChange={handleFilterChange}/><span className="text-xs">{copy.minScoreSuffix}</span></div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 pt-2 border-t sm:grid-cols-2">
                                <input type="text" name="tags" className="p-2 border rounded text-sm" placeholder={copy.customerTagPlaceholder} value={form.target_filter.tags} onChange={handleFilterChange} />
                                <select name="contract_status" className="p-2 border rounded text-sm bg-white" value={form.target_filter.contract_status} onChange={handleFilterChange}>
                                    <option value="">{copy.allStatuses}</option>
                                    <option value="active">{copy.active}</option>
                                    <option value="churned">{copy.churned}</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {/* 本文入力・プレビュー */}
                    <div className="space-y-2">
                        <label className="block text-sm font-bold">{copy.subject}</label>
                        <input type="text" name="subject" required className="w-full p-2 border rounded" placeholder={copy.subjectPlaceholder} value={form.subject} onChange={handleChange} />
                        
                        <div className="flex flex-col gap-2 mt-4 sm:flex-row sm:justify-between sm:items-end">
                            <label className="text-sm font-bold">{copy.body}</label>
                            <div className="flex bg-gray-100 rounded p-1 gap-1">
                                <button type="button" onClick={() => setViewMode('code')} className={`px-3 py-1 text-xs rounded font-bold ${viewMode === 'code' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}>{copy.code}</button>
                                <button type="button" onClick={() => setViewMode('preview')} className={`px-3 py-1 text-xs rounded font-bold ${viewMode === 'preview' ? 'bg-white shadow text-blue-600' : 'text-gray-500'}`}>{copy.preview}</button>
                            </div>
                        </div>
                        {viewMode === 'code' ? (
                            <textarea name="body" required className="w-full h-48 p-4 border rounded font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 outline-none" value={form.body} onChange={handleChange} />
                        ) : (
                            <div className="h-48 border rounded bg-gray-50 p-4 overflow-y-auto"><div dangerouslySetInnerHTML={{ __html: form.body }} className="bg-white p-4 shadow-sm min-h-full prose max-w-none text-sm" /></div>
                        )}
                        <p className="text-[10px] text-gray-400">{copy.availableTags}: {'{{会社名}}'}, {'{{宛名}}'}, {'{{部署名}}'}, {'{{役職}}'}, {'{{担当者名}}'}</p>
                    </div>

                    {/* ★追加：配信タイミング設定エリア */}
                    <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 shadow-inner">
                        <label className="block text-sm font-black text-indigo-900 mb-3 flex items-center gap-2"><SendIcon /> {copy.timing}</label>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            {['draft', 'immediate', 'scheduled'].map(type => (
                                <label key={type} className={`flex-1 flex items-center justify-center gap-2 cursor-pointer bg-white py-3 px-2 rounded-lg border-2 transition-all ${form.delivery_type === type ? 'border-indigo-500 shadow-md ring-1 ring-indigo-500' : 'border-transparent hover:border-gray-200'}`}>
                                    <input type="radio" name="delivery_type" value={type} checked={form.delivery_type === type} onChange={e => setForm({...form, delivery_type: e.target.value})} className="text-indigo-600"/>
                                    <span className="text-sm font-black text-indigo-900">{copy.deliveryTypes[type]}</span>
                                </label>
                            ))}
                        </div>

                        {form.delivery_type === 'scheduled' && (
                            <div className="mt-4 p-3 bg-white rounded-lg border border-indigo-200 animate-fadeIn">
                                <label className="text-xs font-black text-indigo-700 block mb-1">{copy.scheduledAt}</label>
                                <input 
                                    type="datetime-local" 
                                    className="w-full p-2 border rounded-md text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={form.scheduled_at}
                                    onChange={e => setForm({...form, scheduled_at: e.target.value})}
                                    required={form.delivery_type === 'scheduled'}
                                />
                            </div>
                        )}
                    </div>

                    {/* 利用制限の表示 */}
                    {usage && (
                        <div className={`p-3 rounded border text-xs ${isOverLimit ? 'bg-red-50 border-red-300 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                            <p className="font-bold flex items-center gap-1"><InfoIcon className="w-3 h-3"/> {copy.usageTitle}</p>
                            <p>{copy.remaining}: <strong>{remainingSends.toLocaleString()} {pageCopy.unit}</strong> / {copy.monthlyLimit}: {usage.email.limit.toLocaleString()} {pageCopy.unit}</p>
                        </div>
                    )}

                    {/* 下部ボタン */}
                    <div className="flex flex-col gap-3 pt-4 border-t sticky bottom-0 bg-white sm:flex-row sm:justify-between sm:items-center">
                        <button type="button" onClick={() => setShowTestModal(true)} className="w-full justify-center px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 flex items-center gap-2 font-bold text-sm shadow-sm sm:w-auto"><SendIcon /> {copy.testSend}</button>
                        <div className="flex flex-col-reverse gap-2 sm:flex-row">
                            <button type="button" onClick={onClose} className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 font-bold text-sm sm:w-auto">{copy.cancel}</button>
                            <button 
                                type="submit" 
                                disabled={isOverLimit && form.delivery_type !== 'draft'}
                                className={`w-full px-8 py-2 text-white rounded-lg font-black shadow-lg transition-all sm:w-auto ${form.delivery_type === 'draft' ? 'bg-gray-700 hover:bg-gray-800' : 'bg-indigo-600 hover:bg-indigo-700'} disabled:opacity-50`}
                            >
                                {form.delivery_type === 'draft' ? (campaign?.id ? copy.updateSave : copy.saveDraft) : form.delivery_type === 'immediate' ? copy.startNow : copy.schedule}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            {showTestModal && <TestSendModal onClose={() => setShowTestModal(false)} campaignData={form} userEmail={userEmail} isEnglishBeta={isEnglishBeta} />}
        </div>
    );
}

export default function MarketingEmailPage({
    customersPath = '/customers',
    leadsPath = '/marketing/leads',
    trackingPath = '/marketing/tracking',
}) {
    const { user, clientId } = useOutletContext();
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = getEmailCopy(isEnglishBeta);
    const [campaigns, setCampaigns] = useState([]);
    const [issuerProfiles, setIssuerProfiles] = useState([]); // ★追加: 送信元プロフィール一覧
    const [isLoading, setIsLoading] = useState(true);
    
    // モーダル制御
    const [showModal, setShowModal] = useState(false);
    // 送信元設定モーダルは削除し、設定画面へのリンクに変更
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    
    // ★追加: 編集中のキャンペーンを保持するstate
    const [editingCampaign, setEditingCampaign] = useState(null);

    const [usage, setUsage] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [reportTitle, setReportTitle] = useState("");
    const [reportCampaignId, setReportCampaignId] = useState(null);
    const [isReportLoading, setIsReportLoading] = useState(false);

    const fetchData = useCallback(async () => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            // ★修正: client_id を明示的にクエリパラメータとして渡す（管理者操作時の混在防止）
            const resCamp = await fetch(`${API_URL}/api/marketing/campaigns?client_id=${clientId}`, { headers: getAuthHeader() });
            const dataCamp = await resCamp.json();
            if (dataCamp.success) setCampaigns(dataCamp.campaigns);

            // ★追加: 送信元プロフィールも取得（モーダル用）
            const resProf = await fetch(`${API_URL}/api/issuer-profiles?client_id=${clientId}`, { headers: getAuthHeader() });
            const dataProf = await resProf.json();
            if (dataProf.success) setIssuerProfiles(dataProf.profiles);

            // 利用状況 (残り通数確認用)
            const resUsage = await fetch(`${API_URL}/api/usage-status?clientId=${clientId}`, { headers: getAuthHeader() });
            const dataUsage = await resUsage.json();
            if (dataUsage.success && dataUsage.limits) {
                setUsage(dataUsage.limits);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [clientId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCreate = async (formData) => {
        const { delivery_type, scheduled_at, ...restForm } = formData;
        const isUpdate = editingCampaign && editingCampaign.id;
        const url = isUpdate 
            ? `${API_URL}/api/marketing/campaigns/${editingCampaign.id}`
            : `${API_URL}/api/marketing/campaigns`;
        const method = isUpdate ? "PUT" : "POST";

        try {
            // 1. まずは保存（下書き更新）
            const res = await fetch(url, {
                method: method,
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ ...restForm, client_id: clientId })
            });
            
            const saveData = await res.json();
            if (!res.ok) throw new Error(saveData.error || copy.alerts.saveFailed);

            // 新規作成の場合は返ってきたID、更新の場合は既存のIDを使用
            const campaignId = isUpdate ? editingCampaign.id : saveData.id;

            // 2. 配信または予約が選択されている場合のみ、送信APIを叩く
            if (delivery_type === 'immediate' || delivery_type === 'scheduled') {
                const sendRes = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}/send`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...getAuthHeader() },
                    body: JSON.stringify({ 
                        scheduledAt: delivery_type === 'scheduled' ? scheduled_at : null 
                    })
                });
                const sendData = await sendRes.json();
                if (!sendRes.ok) throw new Error(sendData.error || copy.alerts.deliverySetupFailed);
                alert(sendData.message);
            } else {
                alert(isUpdate ? copy.alerts.updated : copy.alerts.draftSaved);
            }

            setShowModal(false);
            setEditingCampaign(null);
            fetchData();

        } catch (e) {
            alert(e.message);
        }
    };

    // ★追加: 削除ボタンクリック時の処理
    const handleDelete = async (campaignId) => {
        if (!window.confirm(copy.alerts.deleteConfirm)) return;
        
        try {
            const res = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId }) // 安全のためclient_idも送信
            });
            if (res.ok) {
                alert(copy.alerts.deleted);
                fetchData();
            } else {
                const data = await res.json();
                alert(`${copy.alerts.deleteFailed}: ${data.error}`);
            }
        } catch (e) {
            alert(copy.alerts.network);
        }
    };

    // ★追加: 編集ボタンクリック時の処理
    const handleEditClick = (campaign) => {
        setEditingCampaign(campaign);
        setShowModal(true);
    };

    // ★追加: 複製ボタンクリック時の処理
    const handleDuplicate = (campaign) => {
        // 元のデータからID、ステータス、実績数を除外して新しいオブジェクトを作成
        const duplicatedData = {
            ...campaign,
            id: null, // 新規作成として扱うためIDを消す
            title: `${campaign.title} (${copy.modal.duplicateSuffix})`, // タイトルにコピーと付ける
            status: 'draft', // 必ず下書き状態にする
            sent_count: 0,   // 送信実績をリセット
            total_targets: 0 // ターゲット数もリセット
        };
        
        setEditingCampaign(duplicatedData);
        setShowModal(true);
    };

    // ★追加: モーダルを閉じる時の処理（編集状態クリア）
    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCampaign(null);
    };

    const handleSend = async (id, title) => {
        if (usage && (usage.email.limit - usage.email.current <= 0)) {
            alert(copy.alerts.limitReached);
            return;
        }

        if (!window.confirm(copy.alerts.sendConfirm(title))) return;
        try {
            const res = await fetch(`${API_URL}/api/marketing/campaigns/${id}/send`, {
                method: "POST",
                headers: getAuthHeader()
            });
            const data = await res.json();
            if (data.success) {
                alert(data.message);
                fetchData();
            } else {
                alert(`${copy.alerts.sendError}: ${data.error}`);
            }
        } catch (e) { alert(e.message); }
    };

    const handleCancel = async (id, title) => {
        if (!window.confirm(copy.alerts.cancelConfirm(title))) return;

        try {
            const res = await fetch(`${API_URL}/api/marketing/campaigns/${id}/cancel`, {
                method: "POST",
                headers: getAuthHeader()
            });
            const data = await res.json();
            if (data.success) {
                alert(data.message);
                fetchData(); // 一覧を更新
            } else {
                alert(`${copy.alerts.error}: ${data.error}`);
            }
        } catch (e) {
            alert(copy.alerts.network);
        }
    };

    const handleShowReport = async (id, title) => {
        setReportTitle(title);
        setReportCampaignId(id);
        setShowReportModal(true);
        setIsReportLoading(true);
        setReportData(null);

        try {
            const res = await fetch(`${API_URL}/api/marketing/campaigns/${id}/report`, { headers: getAuthHeader() });
            const result = await res.json();
            if (result.success) {
                setReportData(result.report);
            } else {
                alert(`${copy.alerts.reportFailed}: ${result.error}`);
                setShowReportModal(false);
            }
        } catch (e) {
            console.error(e);
            alert(copy.alerts.reportNetwork);
            setShowReportModal(false);
        } finally {
            setIsReportLoading(false);
        }
    };

    const renderCampaignStatus = (c) => (
        c.status === 'sending' ? (
            <div className="flex w-32 flex-col">
                <div className="mb-1 flex items-center justify-between">
                    <span className="text-blue-600 font-black text-[10px] uppercase animate-pulse tracking-widest">{copy.status.sending}</span>
                    <span className="text-[10px] text-gray-400 font-mono font-bold">
                        {c.total_targets > 0 ? Math.round((c.sent_count / c.total_targets) * 100) : 0}%
                    </span>
                </div>
                <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden border border-gray-200/50">
                    <div
                        className="bg-blue-500 h-full transition-all duration-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"
                        style={{ width: `${c.total_targets > 0 ? (c.sent_count / c.total_targets) * 100 : 0}%` }}
                    />
                </div>
                <span className="text-[9px] text-gray-400 mt-1 font-bold">
                    {c.sent_count?.toLocaleString()} / {c.total_targets?.toLocaleString()} {copy.unit}
                </span>
            </div>
        ) : (
            <span className={`px-2 py-1 rounded text-xs font-bold ${
                c.status === 'draft' ? 'bg-gray-200 text-gray-700' :
                c.status === 'scheduled' ? 'bg-purple-100 text-purple-700 border border-purple-200' :
                c.status === 'completed' ? 'bg-green-100 text-green-800' :
                'bg-red-100 text-red-800'
            }`}>
                {c.status === 'draft' ? copy.status.draft :
                c.status === 'scheduled' ? copy.status.scheduled :
                c.status === 'completed' ? copy.status.completed : copy.status.failed}
            </span>
        )
    );

    const renderCampaignActions = (c) => (
        <>
            {c.status === 'draft' && (
                <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-center">
                    <button onClick={() => handleEditClick(c)} className="flex items-center justify-center gap-1 bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded text-xs hover:bg-gray-50 font-bold sm:py-1.5">
                        <EditIcon /> {copy.actions.edit}
                    </button>
                    <button onClick={() => handleSend(c.id, c.title)} className="flex items-center justify-center gap-1 bg-indigo-600 text-white px-3 py-2 rounded text-xs hover:bg-indigo-700 font-bold sm:py-1.5">
                        <SendIcon /> {copy.actions.send}
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="col-span-2 flex items-center justify-center gap-1 rounded border border-red-100 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 sm:col-span-1 sm:border-0 sm:px-2">
                        <TrashIcon /> {copy.actions.delete}
                    </button>
                </div>
            )}
            {c.status === 'scheduled' && (
                <button
                    onClick={() => handleCancel(c.id, c.title)}
                    className="flex w-full items-center justify-center gap-1 bg-red-50 text-red-600 border border-red-200 px-3 py-2 rounded text-xs hover:bg-red-100 font-bold sm:w-auto sm:py-1.5"
                >
                    <CloseIcon /> {copy.actions.cancelSchedule}
                </button>
            )}
            {c.status === 'completed' && (
                <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-center">
                    <button
                        onClick={() => handleShowReport(c.id, c.title)}
                        className="flex items-center justify-center gap-1 rounded border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-600 hover:bg-blue-100 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:hover:underline"
                    >
                        <ChartBarIcon /> {copy.actions.report}
                    </button>
                    <button
                        onClick={() => handleDuplicate(c)}
                        className="flex items-center justify-center gap-1 text-gray-600 hover:text-indigo-600 text-xs font-bold border border-gray-300 px-3 py-2 rounded hover:bg-gray-50 sm:px-2 sm:py-1"
                    >
                        <CopyIcon /> {copy.actions.duplicate}
                    </button>
                </div>
            )}
        </>
    );

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{copy.pageTitle}</h1>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex">
                        {!isEnglishBeta && (
                            <a
                                href="https://gemini.google.com/gem/1ZWnJbH7arFMPx8VMYv_8FxBaBkuEAKgk?usp=sharing"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center gap-2 bg-blue-100 text-blue-700 border border-blue-200 px-4 py-2 rounded hover:bg-blue-200 font-bold shadow-sm transition-colors"
                            >
                                <MagicIcon /> {copy.openGem}
                            </a>
                        )}
                        {/* ★追加: アシスト機能へのボタン */}
                        {!isEnglishBeta && (
                            <button onClick={() => setShowPromptModal(true)} className="flex items-center justify-center gap-2 bg-purple-100 text-purple-700 border border-purple-200 px-4 py-2 rounded hover:bg-purple-200 font-bold shadow-sm transition-colors">
                                <MagicIcon /> {copy.htmlAssist}
                            </button>
                        )}
                        {/* ★修正: モーダル起動ではなく設定画面へのリンクに変更 */}
                        <Link to={withOperatorSurface(trackingPath)} className="flex items-center justify-center gap-2 bg-white text-gray-700 border px-4 py-2 rounded hover:bg-gray-50 font-bold shadow-sm">
                            <SettingsIcon /> {copy.senderSettings}
                        </Link>
                        <button onClick={() => { setEditingCampaign(null); setShowModal(true); }} className="flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-2 px-4 rounded hover:bg-blue-700">
                            <PlusIcon /> {copy.newCampaign}
                        </button>
                    </div>
                </div>

                {/* 通数制限インフォメーション */}
                <div className="bg-white p-4 rounded shadow-sm mb-6 border-l-4 border-indigo-500 flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                    <div>
                        <p className="text-sm text-gray-600 font-bold">{copy.usageTitle}</p>
                        <p className="text-2xl font-bold text-indigo-700">
                            {usage ? usage.email.current.toLocaleString() : '-'} 
                            <span className="text-sm text-gray-500 font-normal mx-1">/</span>
                            {usage ? usage.email.limit.toLocaleString() : '-'} {copy.unit}
                        </p>
                    </div>
                </div>
                

                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="divide-y divide-gray-100 md:hidden">
                        {isLoading ? <LoadingSpinner /> :
                         campaigns.length === 0 ? <div className="p-8 text-center text-gray-500">{copy.noCampaigns}</div> :
                         campaigns.map(c => (
                            <div key={`${c.id}-mobile`} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="break-words font-bold text-gray-900">{c.title}</p>
                                        <p className="mt-1 break-words text-xs text-gray-500">{c.subject || '-'}</p>
                                    </div>
                                    <div className="shrink-0">{renderCampaignStatus(c)}</div>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                    <p><span className="font-semibold text-gray-500">{copy.mobileSent}</span><br />{c.status === 'completed' ? `${c.sent_count} / ${c.total_targets}` : '-'}</p>
                                    <p><span className="font-semibold text-gray-500">{copy.mobileCreated}</span><br />{new Date(c.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="mt-4">{renderCampaignActions(c)}</div>
                            </div>
                         ))}
                    </div>
                    <div className="hidden overflow-x-auto md:block">
                        <table className="w-full text-sm text-left text-gray-600">
                            <thead className="bg-gray-100 text-gray-700 uppercase">
                                <tr>
                                    <th className="p-4">{copy.headers[0]}</th>
                                    <th className="p-4">{copy.headers[1]}</th>
                                    <th className="p-4">{copy.headers[2]}</th>
                                    <th className="p-4">{copy.headers[3]}</th>
                                    <th className="p-4">{copy.headers[4]}</th>
                                    <th className="p-4 text-center">{copy.headers[5]}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {isLoading ? <tr><td colSpan="6"><LoadingSpinner /></td></tr> :
                                 campaigns.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-gray-500">{copy.noCampaigns}</td></tr> :
                                 campaigns.map(c => (
                                    <tr key={c.id} className="hover:bg-gray-50">
                                        <td className="p-4">
                                            {renderCampaignStatus(c)}
                                        </td>
                                        <td className="p-4 font-bold">{c.title}</td>
                                        <td className="p-4 truncate max-w-xs">{c.subject}</td>
                                        <td className="p-4">
                                            {c.status === 'completed' ? (
                                                <span className="font-mono">{c.sent_count} / {c.total_targets}</span>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="p-4">{new Date(c.created_at).toLocaleDateString()}</td>
                                        <td className="p-4 text-center">
                                            {renderCampaignActions(c)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {/* ★ userEmail, campaign, issuerProfiles を渡すように修正 */}
            {showModal && <CampaignModal onClose={handleCloseModal} onSave={handleCreate} usage={usage} userEmail={user?.email} openPromptGenerator={() => setShowPromptModal(true)} campaign={editingCampaign} issuerProfiles={issuerProfiles} isEnglishBeta={isEnglishBeta} />}
            {/* ★追加: レポートモーダルの表示 */}
            {showReportModal && <ReportModal onClose={() => setShowReportModal(false)} data={reportData} isLoading={isReportLoading} title={reportTitle} campaignId={reportCampaignId} isEnglishBeta={isEnglishBeta} customersPath={customersPath} leadsPath={leadsPath} />}

            {/* ★追加: AIプロンプト生成モーダル */}
            {showPromptModal && !isEnglishBeta && (
                <HtmlPromptGeneratorModal 
                    onClose={() => setShowPromptModal(false)} 
                    isEnglishBeta={isEnglishBeta}
                />
            )}
        </div>
    );
}
